import React, { useState } from 'react';
import {
    Box,
    Button,
    Popover,
    Typography,
    Tooltip,
    Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LLMModel } from '../../store/constants/llmModels';
import { ModelCategory } from '../../../hooks/useModelSwitcher';

// ─────────────────────────────────────────────────────────────────────────────
// ModelSwitcher — "Model Control" popover
//
// Supports independent selection for Reasoning and Quick Response categories.
// ─────────────────────────────────────────────────────────────────────────────

interface ModelSwitcherProps {
    reasoningModel: LLMModel | null;
    quickModel: LLMModel | null;
    availableModels: {
        explanatory: LLMModel[];
        reasoning: LLMModel[];
    };
    onModelChange: (model: LLMModel, category: ModelCategory) => void;
    isOverridden?: boolean;
    isDualMode?: boolean;
}

const ModelSwitcher: React.FC<ModelSwitcherProps> = ({
    reasoningModel,
    quickModel,
    availableModels,
    onModelChange,
    isOverridden = false,
    isDualMode = true,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen  = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleSelect = (model: LLMModel, category: ModelCategory) => {
        onModelChange(model, category);
        handleClose();
    };

    // ── Shared row renderer ──────────────────────────────────────────────────
    const ModelRow = ({ model, category }: { model: LLMModel; category: ModelCategory }) => {
        const currentModel = category === 'reasoning' ? reasoningModel : quickModel;
        const isActive = currentModel?.id === model.id;
        
        return (
            <Box
                key={model.id}
                onClick={() => handleSelect(model, category)}
                sx={{
                    px: 2,
                    py: 0.9,
                    cursor: 'pointer',
                    backgroundColor: isActive ? '#f5f8ff' : 'transparent',
                    borderLeft: isActive ? '3px solid #4f46e5' : '3px solid transparent',
                    transition: 'background-color 0.12s',
                    '&:hover': { backgroundColor: isActive ? '#eef2ff' : '#f9fafb' },
                }}
            >
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#4338ca' : '#101828',
                        lineHeight: 1.4,
                    }}
                >
                    {model.label}
                </Typography>
                {model.description && (
                    <Typography sx={{ fontSize: '11.5px', color: '#667085', lineHeight: 1.4, mt: 0.1 }}>
                        {model.description}
                    </Typography>
                )}
            </Box>
        );
    };

    const sectionLabel = (text: string) => (
        <Box sx={{ px: 2, pt: 1.2, pb: 0.4 }}>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#667085',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                }}
            >
                {text}
            </Typography>
        </Box>
    );

    const tooltipContent = (
        <Box sx={{ p: 0.5 }}>
            {isDualMode ? (
                <>
                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        <strong>Reasoning:</strong> {reasoningModel?.label || 'Platform Default'}
                    </Typography>
                    <Typography variant="caption" display="block">
                        <strong>Quick Response:</strong> {quickModel?.label || 'Platform Default'}
                    </Typography>
                </>
            ) : (
                <Typography variant="caption" display="block">
                    <strong>{reasoningModel ? 'Reasoning' : (quickModel ? 'Quick Response' : 'Model')}:</strong>{' '}
                    {reasoningModel?.label || quickModel?.label || 'Platform Default'}
                </Typography>
            )}
        </Box>
    );

    // Button label reflects active overrides
    const getButtonLabel = () => {
        if (isDualMode) {
            if (reasoningModel && quickModel) return `${reasoningModel.label} + ${quickModel.label}`;
            if (reasoningModel) return reasoningModel.label;
            if (quickModel) return quickModel.label;
            return 'Default Models';
        } else {
            return reasoningModel?.label || quickModel?.label || 'Default Model';
        }
    };

    return (
        <>
            {/* ── Trigger Button ─────────────────────────────────────────── */}
            <Tooltip title={tooltipContent} arrow placement="bottom">
                <Button
                    id="model-switcher-btn"
                    size="small"
                    variant="outlined"
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={handleOpen}
                    sx={{
                        height: '26px',
                        fontSize: '11px', // Slightly smaller font for dual names
                        fontWeight: 500,
                        textTransform: 'none',
                        px: 1,
                        pr: 0.8,
                        width: '100%',
                        justifyContent: 'space-between',
                        borderColor: isOverridden ? '#818cf8' : '#d0d5dd',
                        color: isOverridden ? '#4338ca' : '#344054',
                        backgroundColor: isOverridden ? '#eef2ff' : '#fff',
                        borderRadius: '6px',
                        minWidth: 'unset',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        letterSpacing: 0,
                        '&:hover': {
                            borderColor: isOverridden ? '#6366f1' : '#98a2b3',
                            backgroundColor: isOverridden ? '#e0e7ff' : '#f9fafb',
                        },
                        '& .MuiButton-endIcon': { ml: 0.4 },
                        '& .MuiButton-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                    }}
                >
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getButtonLabel()}
                    </Box>
                </Button>
            </Tooltip>

            {/* ── Model Control Popover ───────────────────────────────────── */}
            <Popover
                id="model-control-popover"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        mt: 0.5,
                        width: 300,
                        borderRadius: '10px',
                        border: '1px solid #e4e7ec',
                        overflow: 'hidden',
                    },
                }}
            >
                {/* Pinned header */}
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f2f4f7', backgroundColor: '#fff' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#101828', letterSpacing: '-0.01em' }}>
                        Model Control
                    </Typography>
                </Box>

                {/* Scrollable list */}
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        maxHeight: 320,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#d0d5dd', borderRadius: '4px' },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    }}
                >
                    {/* Quick Response */}
                    {sectionLabel('Quick Response')}
                    {availableModels.explanatory.map(m => <ModelRow key={m.id} model={m} category="explanatory" />)}

                    <Divider sx={{ mx: 2, my: 1, borderColor: '#f2f4f7' }} />

                    {/* Reasoning */}
                    {sectionLabel('Reasoning')}
                    {availableModels.reasoning.map(m => <ModelRow key={m.id} model={m} category="reasoning" />)}

                    <Box sx={{ pb: 1 }} />
                </Box>
            </Popover>
        </>
    );
};

export default ModelSwitcher;
